<template>
  <component
    :is="as"
    v-bind="$attrs"
    :class="[
      $style.topNavItem,
      {
        [$style.selected]: selected,
        [$style.disabled]: disabled,
        [$style.appearanceInverted]: appearance === 'inverted',
        [$style.hasIcon]: icon,
        [$style.modeSymbolAndText]: viewMode === 'symbolAndText',
        [$style.modeSymbol]: viewMode === 'symbol',
        [$style.active]: active,
      },
    ]"
  >
    <SCASvgIcon v-if="icon" :icon="icon" :size="iconSize" />
    <span v-if="label" :class="[$style.label, { [$style.labelCompact]: viewMode === 'symbolAndText' }]">
      {{ label }}
    </span>
    <div
      v-if="hasActionIcon"
      :class="[
        $style.actionIcon,
        {
          [$style.actionIconActive]: actionIconActive,
          [$style.modeSymbolAndText]: viewMode === 'symbolAndText',
          [$style.modeSymbol]: viewMode === 'symbol',
        },
      ]"
    >
      <slot name="actionIcon" />
    </div>
  </component>
</template>

<script setup lang="ts">
import type { Component, VNode } from "vue";
import { Comment, computed, useSlots } from "vue";

import type { IconName } from "./icons/iconNames.generated";
import SCASvgIcon from "./SCASvgIcon.vue";

defineOptions({
  inheritAttrs: false,
});

const props = withDefaults(
  defineProps<{
    label?: string;
    selected?: boolean;
    disabled?: boolean;
    icon?: IconName;
    as?: string | Component;
    appearance?: "normal" | "inverted";
    viewMode?: "text" | "symbol" | "symbolAndText";
    actionIconActive?: boolean;
    active?: boolean;
  }>(),
  {
    label: undefined,
    selected: false,
    disabled: false,
    icon: undefined,
    as: "button",
    appearance: "normal",
    viewMode: "text",
    actionIconActive: false,
    active: false,
  },
);

const slots = useSlots();

const iconSize = computed(() => (props.viewMode === "symbolAndText" ? "14px" : "20px"));

const hasActionIcon = computed(() => {
  const slot = slots.actionIcon?.({});
  if (!slot) return false;

  return slot.some((vnode: VNode) => vnode.type !== Comment);
});
</script>

<style lang="scss" module>
@use "../styles/generated/figma_typo_mixins.scss" as FigmaTypoMixins;
@use "../styles/icon.scss" as IconMixins;

.topNavItem {
  @include FigmaTypoMixins.typo-body-medium;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--border-radius-large);
  height: var(--dimension-350);
  padding: 0 var(--space-component-padding-medium);
  color: var(--color-action-neutral-normal);
  position: relative;
  white-space: nowrap;
  transition: background-color 250ms ease-in-out;
  @include IconMixins.icon-color(currentColor);

  &.hasIcon {
    flex-direction: column;
  }

  &.modeSymbolAndText {
    gap: 1px;
    padding: 2px 4px;
    overflow: hidden;
  }

  &.modeSymbol {
    padding: 4px;
  }

  &:focus-visible,
  &:hover {
    color: var(--color-action-neutral-hover);
    background-color: var(--color-action-ghost-hover);
    outline: none;
    .actionIcon {
      opacity: 1;
      background-color: color-mix(in srgb, var(--color-semantic-brand-m4) 70%, transparent);
    }
  }

  &:not(.disabled, [disabled]) {
    &:active,
    &.active {
      color: var(--color-action-neutral-active);
      background-color: var(--color-action-ghost-active);
    }
  }

  &.selected {
    color: var(--color-action-neutral-normal);
    background-color: var(--color-action-primary-subtle-selected);
  }

  &.disabled,
  &[disabled] {
    color: var(--color-action-neutral-normal);
    background-color: var(--color-action-ghost-normal);
    opacity: 0.5;
    cursor: default;
  }

  &.appearanceInverted {
    color: var(--color-action-inverted-normal);
    color: var(--color-whiteA-0);

    &:hover {
      color: var(--color-action-inverted-hover);
      background-color: color-mix(in srgb, var(--color-semantic-brand-m4) 50%, transparent);
    }

    &:not(.disabled, [disabled]) {
      &:active,
      &.active {
        color: var(--color-action-inverted-active);
        background-color: color-mix(in srgb, var(--color-semantic-brand-m4) 50%, transparent);
      }
    }

    &.selected {
      color: var(--color-action-inverted-selected);
      background-color: var(--color-action-primary-selected);

      &:hover {
        background-color: color-mix(in srgb, var(--color-semantic-brand-m4) 50%, transparent);
      }
    }

    &.disabled,
    &[disabled] {
      color: var(--color-content-disabled-inverted);
      background-color: transparent;
    }
  }

  .labelCompact {
    @include FigmaTypoMixins.typo-body-xsmall;
    font-size: 9px;
    line-height: 9px;
    letter-spacing: 0.18px;
    width: 52px;
    text-align: center;
  }

  .actionIcon {
    position: absolute;
    top: 0;
    left: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    width: 16px;
    opacity: 0;
    background-color: color-mix(in srgb, var(--color-semantic-brand-m4) 70%, transparent);
    border-radius: 8px 0 0 8px;
    transition:
      background-color 250ms ease-in-out,
      opacity 250ms ease-in-out;

    &.modeSymbolAndText {
      width: 20px;
    }

    &.modeSymbol {
      width: 100%;
      height: 9px;
      top: unset;
      bottom: 0;
      border-radius: 0 0 8px 8px;

      svg {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      }
    }

    svg {
      --size: 9px !important;
    }

    &:hover,
    &.actionIconActive {
      opacity: 1;
      background-color: var(--color-semantic-brand-m4);
    }
  }
}
</style>
